import React, { useEffect, useMemo, useState } from 'react';
import './style.less';
import { useLiveListenContext } from '../LiveListen/livelisten-provider';
import { Button } from 'shared/components/Button';
import { AppState } from 'shared/store/reducers';
import { useAppSelector } from 'shared/store';
import { animated, useSpring } from '@react-spring/web';
import { useLocation } from 'react-router';
import { useDrag } from 'react-use-gesture';
import { Icon } from 'shared/components/Icon';
import { useDispatch, useSelector } from 'react-redux';
import { Tooltip } from 'shared/components/Tooltip';
import { LiveListenState } from 'types/livelisten';
import { LiveListenStatus } from '../LiveListenStatus';
import { setAutoListenState } from 'shared/store/livelisten-slice';
import { CallTaskStatus } from 'types/outbound';
import clsx from 'clsx';
import { CallDuration } from '../CallDuration';

export const LiveListenActiveCall = () => {
  const { disconnect, listenedSession } = useLiveListenContext();
  const { liveListenDialSessionId, salesfloorRoomId } = useAppSelector(
    (state: AppState) => state.livelisten,
  );
  const { autoListenState, autoListenDelay } = useSelector(
    (state: AppState) => state.livelisten,
  );
  const liveListenState = useAppSelector(
    (state: AppState) => state.livelisten.autoListenState,
  );
  const { pathname } = useLocation();
  const dispatch = useDispatch();
  const autoListenEnabled = liveListenState === LiveListenState.ON;
  const isListening = useMemo(() => {
    return (
      liveListenDialSessionId &&
      listenedSession?.activeCall?.status === CallTaskStatus.ANSWERED
    );
  }, [listenedSession?.activeCall?.status, liveListenDialSessionId]);
  const [shouldRender, setShouldRender] = useState(autoListenEnabled || isListening);

  useEffect(() => {
    setShouldRender(autoListenEnabled || isListening);
  }, [autoListenEnabled, isListening]);

  const fadeSpring = useSpring({
    opacity: autoListenEnabled || isListening ? 1 : 0,
    transform: autoListenEnabled || isListening ? 'scale(1)' : 'scale(0.95)',
    config: { tension: 300, friction: 20 },
    onRest: () => {
      if (!autoListenEnabled && !isListening) setShouldRender(false);
    },
  });
  const [{ x, y }, api] = useSpring(() => ({ x: 0, y: 0 }));
  const springProps = useSpring({ x, y });

  const bind = useDrag(
    ({ down, offset: [ox, oy] }) => api.start({ x: ox, y: oy, immediate: down }),
    {
      bounds: {
        left: 0,
        bottom: 0,
        right: window.innerWidth - 568,
        top:
          (window.innerHeight -
            64 -
            (document.querySelector('.LiveListenActiveCall')?.clientHeight ?? 168)) *
          -1,
      },
    },
  );

  // Don't render the widget on the Live Listen screen
  if (['/livelisten'].some(p => pathname.startsWith(p))) {
    return null;
  }

  return shouldRender ? (
    <animated.div
      className={'LiveListenActiveCall'}
      style={{
        ...springProps,
        ...fadeSpring,
        x: springProps.x.to(x => `${x}px`),
        y: springProps.y.to(y => `${y}px`),
      }}
    >
      <div className="LiveListenActiveCall__drag" {...bind()}>
        <Icon width={14} height={14} icon="drag" />
      </div>
      <div className="LiveListenActiveCall__wrap">
        <div className="LiveListenActiveCall__content">
          <span className="LiveListenActiveCall__content-header">Live Listen</span>
          {isListening && (
            <div className="LiveListenActiveCall__metadataWrap">
              <div className="LiveListenActiveCall__metadata">
                <span className="LiveListenActiveCall__metadata-agent">
                  {listenedSession?.agent?.displayName}
                </span>
                <span className="LiveListenActiveCall__metadata-prospect">
                  Prospect:{' '}
                  {listenedSession?.prospect?.name || listenedSession?.prospect?.phone}
                </span>
              </div>
            </div>
          )}
          {isListening && (
            <div className="LiveListenActiveCall__contentWrap">
              <div className="LiveListenActiveCall__contentItem">
                <div className="LiveListenActiveCall__pills">
                  <div className="LiveListenActiveCall__pill">
                    <Icon icon="phoneOutline" width={12} height={12} />
                    Call in Progress
                  </div>
                  <div className="LiveListenActiveCall__pill LiveListenActiveCall__pill--duration">
                    {listenedSession?.callStartedAt && (
                      <CallDuration callStartedAt={listenedSession?.callStartedAt} />
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {!isListening && (
            <div className="LiveListenActiveCall__liveListenStatus">
              <LiveListenStatus
                enabled={autoListenState === LiveListenState.ON}
                delay={autoListenDelay}
              />
            </div>
          )}
        </div>
        <div className="LiveListenActiveCall__actions">
          {!isListening && (
            <div className="LiveListenActiveCall__actions--action">
              <Tooltip title="Stop listening" theme={'dark'}>
                <Button
                  type="ghost"
                  size="middle"
                  className="LiveListenActiveCall__button LiveListenActiveCall__button-turnOff"
                  onClick={() => {
                    dispatch(setAutoListenState(LiveListenState.OFF));
                  }}
                >
                  Turn off
                </Button>
              </Tooltip>
            </div>
          )}
          {isListening && (
            <div className="LiveListenActiveCall__actions--action">
              <Tooltip title="Stop listening" theme={'dark'}>
                <Button
                  type="ghost"
                  size="middle"
                  className="LiveListenActiveCall__button LiveListenActiveCall__button-stop"
                  onClick={() => {
                    disconnect(liveListenDialSessionId, salesfloorRoomId);
                  }}
                >
                  <Icon icon="stop" width={24} height={24} />
                </Button>
              </Tooltip>
            </div>
          )}
          <div className="LiveListenActiveCall__actions--action">
            <Tooltip title="Go to Live Listen" theme={'dark'}>
              <Button
                type="ghost"
                size="middle"
                className="LiveListenActiveCall__button LiveListenActiveCall__button-autoListenLink"
                to="/livelisten"
              >
                <Icon
                  icon="liveListenIcon"
                  className={clsx(autoListenState === LiveListenState.OFF && 'inactive')}
                  width={24}
                  height={24}
                />
              </Button>
            </Tooltip>
          </div>
        </div>
      </div>
    </animated.div>
  ) : null;
};
